/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC } from 'react';
import { Controller, UseFormReturn, useFormContext } from 'react-hook-form';
import { FormDataType, PreferredMethod } from 'contexts/FormProvider';
import { useContent, useContentContext } from '@qbe/dfc-content';
import { AddressAutocomplete } from 'components';
import { TextField, FormGroupLabel } from '@qbe/ads-core';
import { Toggle } from '@qbe/ads-blocks-forms';
import AddressManual from 'components/AddressManual/AddressManual';
import ContactNumber from 'components/ContactNumber/ContactNumber';
import { FormSpacer, CmsAddressSearch } from 'components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContactOptionsAddressHandler } from 'hooks/useContactOptionsAddressHandler';
interface Props {
  form: UseFormReturn<FormDataType>;
}

interface AddressFieldConfig {
  label: string;
  fieldName: 'physicalAddress' | 'postalAddress';
  fieldNameIsValid: 'isPhysAddressValid' | 'isPosAddressValid';
  fieldNameIsManual: 'isManualPhysAddress' | 'isManualPosAddress';
  linkType: 'physical' | 'postal';
  prefix: 'phy' | 'pos';
  isManual: boolean;
  isOptional?: boolean;
}

// Remove this component once the QAS migration toggle is removed from production. This is a temporary solution to support both the new and old address input methods.
const AddressSection: FC<{ config: AddressFieldConfig; form: UseFormReturn<FormDataType> }> = ({ config, form }) => {
  const { label, fieldName, fieldNameIsValid, fieldNameIsManual, linkType, prefix, isManual, isOptional } = config;

  return (
    <>
      {!isManual && (
        <AddressAutocomplete
          form={form}
          label={label}
          fieldName={fieldName}
          fieldNameIsValid={fieldNameIsValid}
          fieldNameIsManual={fieldNameIsManual}
          dataTrackValue="mask"
          isOptional={isOptional}
          linkType={linkType}
          addressFieldNames={{
            country: `${prefix}Country`,
            streetAddress: `${prefix}StreetAddress`,
            suburb: `${prefix}Suburb`,
            state: `${prefix}State`,
            postCode: `${prefix}Postcode`,
          }}
        />
      )}
      {isManual && <AddressManual prefix={prefix} form={form} />}
    </>
  );
};

const ContactOptions: FC<Props> = (props) => {
  const { form } = props;

  const {
    watch,
    control,
    setValue,
    trigger: triggerValidation,
    formState: { errors },
  } = form;

  // TODO: Remove QAS migration toggle after DcomCustQasMigrationToggle is disabled in production.
  const { DcomCustQasMigrationToggle = true } = useFlags();
  const isComplaint = watch('lodgement') === 'Complaint';
  const addressType = watch('addressType') || '';
  const iContactPreferedMethod = watch('iContactPreferedMethod') || '';

  // Use address handler hook for address-specific logic
  const {
    showPostalAddress,
    handleAddressChange,
    physicalDefaults,
    postalDefaults,
    isManualPhysAddress,
    isManualPosAddress,
  } = useContactOptionsAddressHandler({
    form,
    DcomCustQasMigrationToggle,
    isComplaint,
  });

  // UI helper functions for contact method validation
  const requiredChecker = (field: PreferredMethod) =>
    iContactPreferedMethod === field || (!isComplaint && field === 'email') ? ` *` : '';

  const requiredCheckerNew = (field: PreferredMethod) =>
    iContactPreferedMethod === field || (!isComplaint && field === 'email') ? true : false;

  const handleAddressTypeChange = (e: React.ChangeEvent<HTMLInputElement>, field: any) => {
    const value = e.target.value;
    setValue('addressType', value, { shouldValidate: true });
    triggerValidation('addressType');

    if (value === 'yes') {
      setValue('postalAddress', null, { shouldValidate: true });
      setValue('isManualPosAddress', false, { shouldValidate: true });
      setValue('postalAddressObject', null, { shouldValidate: true });
      setValue('posStreetAddress', '', { shouldValidate: true });
      setValue('posSuburb', '', { shouldValidate: true });
      setValue('posCountry', '', { shouldValidate: true });
      setValue('posState', '', { shouldValidate: true });
      setValue('posPostcode', '', { shouldValidate: true });
    }
    field.onChange(value);
  };

  const { getTextContent } = useContent({ context: useContentContext() });
  // Localized content constants
  const emailTitle = getTextContent('consent.contactDetails.email');
  const physicalAddressTitle = getTextContent('insured.contactDetails.physicalAddress');
  const phoneNumberTitle = getTextContent('consent.contactDetails.phoneNumber');
  const addressTypeTitle = getTextContent('consent.contactDetails.addressType');

  return (
    <>
      <FormGroupLabel required={requiredCheckerNew('email')} title={emailTitle} alignCenter />
      <FormSpacer type="input" />
      <Controller
        name="cEmailAddress"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            title={`${emailTitle}${requiredChecker('email')}`}
            error={!!errors.cEmailAddress}
            value={field.value as string | undefined}
            label="Email address"
            id="cEmailAddress"
            data-track="cEmailAddress"
            data-track-value="mask"
            maxLength={100}
            helperText={errors.cEmailAddress ? errors.cEmailAddress?.message : null}
          />
        )}
      />

      {/** physical address */}
      <FormGroupLabel required title={physicalAddressTitle} alignCenter />
      <FormSpacer type="input" />
      {DcomCustQasMigrationToggle ? (
        <CmsAddressSearch
          type="physical"
          required={isComplaint}
          defaultAddress={physicalDefaults.addressObject ?? undefined}
          defaultManualAddress={physicalDefaults.defaultManualAddress ?? undefined}
          isManualAddress={physicalDefaults.isManual}
          onAddressChange={handleAddressChange}
        />
      ) : (
        <AddressSection
          config={{
            label: 'Residential address',
            fieldName: 'physicalAddress',
            fieldNameIsValid: 'isPhysAddressValid',
            fieldNameIsManual: 'isManualPhysAddress',
            linkType: 'physical',
            prefix: 'phy',
            isManual: isManualPhysAddress,
            isOptional: !isComplaint,
          }}
          form={form}
        />
      )}

      {/** addressType */}
      {isComplaint && (
        <>
          <Controller
            name="addressType"
            control={control}
            rules={{ required: '' }}
            render={({ field }) => (
              <Toggle
                {...field}
                title={addressTypeTitle}
                required
                defaultValue={addressType || ''}
                data-track="addressType"
                data-track-value="capture"
                optionList={[
                  {
                    autoComplete: 'off',
                    id: 'addressType_yes',
                    label: 'Yes',
                    value: 'yes',
                    defaultChecked: 'yes' === addressType,
                  },
                  {
                    autoComplete: 'off',
                    id: 'addressType_no',
                    label: 'No',
                    value: 'no',
                    defaultChecked: 'no' === addressType,
                  },
                ]}
                onChange={(e) => handleAddressTypeChange(e, field)}
              />
            )}
          />
        </>
      )}

      {/** postalAddress */}
      {showPostalAddress && (
        <>
          <FormSpacer type="input" />
          {DcomCustQasMigrationToggle ? (
            <CmsAddressSearch
              type="postal"
              required
              defaultAddress={postalDefaults.addressObject ?? undefined}
              defaultManualAddress={postalDefaults.defaultManualAddress ?? undefined}
              isManualAddress={postalDefaults.isManual}
              onAddressChange={handleAddressChange}
            />
          ) : (
            <AddressSection
              config={{
                label: 'Postal address',
                fieldName: 'postalAddress',
                fieldNameIsValid: 'isPosAddressValid',
                fieldNameIsManual: 'isManualPosAddress',
                linkType: 'postal',
                prefix: 'pos',
                isManual: isManualPosAddress,
              }}
              form={form}
            />
          )}
        </>
      )}

      {/** contactNumber */}

      <FormGroupLabel required={requiredCheckerNew('phone')} title={phoneNumberTitle} alignCenter />
      <FormSpacer type="input" />
      <ContactNumber form={form} />
    </>
  );
};

export default ContactOptions;
